import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { PRODUCT_CATEGORIES } from "@/constants/assets";

export const ProductsShowcase = () => {
  return (
    <section
      id="products"
      data-testid="products-showcase"
      className="relative bg-[#0a0a0c] pt-10 pb-24 lg:pt-14 lg:pb-32"
    >
      <div className="mx-auto max-w-[1600px] px-6 lg:px-16">
        <div className="mb-12 flex flex-col items-center gap-6 lg:mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1] }}
            className="font-serif-display text-center text-5xl font-bold tracking-[-0.02em] text-white lg:text-7xl"
            data-testid="products-showcase-heading"
          >
            Products.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.19, 1, 0.22, 1] }}
            className="max-w-xl text-center font-body text-sm leading-relaxed text-white/55"
          >
            From heritage-inspired steel doors and slim FerroFinestra windows
            to bespoke luxury entrances — every product line begins with the
            same slim, uncompromising profile.
          </motion.p>
        </div>

        <div
          data-testid="products-showcase-grid"
          className="mx-auto grid max-w-[1200px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 lg:gap-8"
        >
          {PRODUCT_CATEGORIES.map((c, i) => (
            <motion.a
              key={c.slug}
              href={c.href}
              data-testid={`product-card-${c.slug}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.9,
                delay: 0.1 + i * 0.08,
                ease: [0.19, 1, 0.22, 1],
              }}
              className="group relative block aspect-[3/4] overflow-hidden rounded-sm border-2 border-white/20 bg-black shadow-[0_0_0_1px_rgba(198,168,124,0)] transition-all duration-500 hover:border-[#c6a87c] hover:shadow-[0_0_0_1px_rgba(198,168,124,0.6),0_25px_60px_-15px_rgba(198,168,124,0.45)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c6a87c]"
            >
              <img
                src={c.image}
                alt={c.title}
                draggable={false}
                style={{ objectPosition: c.objectPosition || "center" }}
                className="absolute inset-0 h-full w-full select-none object-cover transition-all duration-[900ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.06] group-hover:blur-[6px]"
              />

              {/* Dark gradient wash */}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10 transition-opacity duration-700 group-hover:from-black/70" />

              {/* Bronze reveal border */}
              <div className="pointer-events-none absolute inset-0 border-2 border-transparent transition-colors duration-500 group-hover:border-[#c6a87c]" />

              {/* Hover darken layer */}
              <div className="pointer-events-none absolute inset-0 bg-black/0 transition-colors duration-700 group-hover:bg-black/45" />

              {/* Centred title */}
              <div className="absolute inset-0 flex items-center justify-center px-6 text-center">
                <h3 className="font-serif-display text-2xl font-bold tracking-[-0.01em] text-white transition-all duration-500 group-hover:-translate-y-6 group-hover:opacity-90 lg:text-3xl">
                  {c.title}
                </h3>
              </div>

              {/* Explore Range — hidden by default, fades in on hover */}
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-6 text-center">
                <span className="mt-16 inline-flex items-center gap-2 rounded-full border border-[#c6a87c] bg-[#c6a87c]/10 px-6 py-2.5 font-tech text-[11px] font-semibold uppercase tracking-[0.32em] text-[#c6a87c] opacity-0 backdrop-blur-sm transition-all duration-500 group-hover:opacity-100">
                  Explore Range
                  <span aria-hidden className="transition-transform duration-500 group-hover:translate-x-1">
                    →
                  </span>
                </span>
              </div>
            </motion.a>
          ))}
        </div>

        {/* Primary shop CTA — drives clicks straight into the full catalogue */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.19, 1, 0.22, 1] }}
          className="mt-16 flex flex-col items-center gap-4 lg:mt-20"
        >
          <Link
            to="/products"
            data-testid="products-showcase-shop-cta"
            className="inline-flex items-center rounded-full bg-[#c6a87c] px-10 py-4 font-tech text-[12px] font-bold uppercase tracking-[0.28em] text-[#0a0a0c] shadow-[0_0_0_1px_rgba(198,168,124,0.4),0_20px_45px_-10px_rgba(198,168,124,0.55)] transition-all duration-500 hover:bg-[#d8bd93] hover:shadow-[0_0_0_1px_rgba(198,168,124,0.6),0_25px_55px_-8px_rgba(198,168,124,0.8)]"
          >
            Shop All Products
          </Link>
        </motion.div>
      </div>
    </section>
  );
};
